import Auth from '@aws-amplify/auth';
import AppBar, { AppBarProps } from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Theme } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Dialog from '@material-ui/core/Dialog';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import SettingsIcon from '@material-ui/icons/Settings';
import { makeStyles } from '@material-ui/styles';
import { memo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthUser } from '../../../contexts/AuthProvider/hooks';
import {
  hasUserOrgAnyRole,
  Role,
  hasRoleInAnyUsersOrg,
  hasOneOfOrgRoles,
} from '../../../contexts/AuthProvider/types';
import {
  MediaUp,
  MediaDown,
} from '../../../components/MediaQueries/MediaQueries';
import {
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Switch,
  Paper,
  Typography,
  InputBase,
  Tooltip,
} from '@material-ui/core';
import { useManageContext } from '../../../contexts/ManageContext/ManageContext';
import Visible from '../../../components/Visible/Visible';
import { useDeviceSearch, useDeviceScan } from './hooks';
import VisibleToRoles from '../../../components/VisibleToRoles/VisibleToRoles';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOrgs from './components/MenuOrgs/MenuOrgs';
import DevicesIcon from '@material-ui/icons/PhoneAndroid';
import DashboardIcon from '@material-ui/icons/PieChart';
import TargetSystemsIcon from '@material-ui/icons/DeviceHub';
import Scanner from '../../../components/Barcode/Scanner';
import Version from '../Version/Version';
import LockIcon from '@material-ui/icons/Lock';
import ChangePasswordDialog from '../ChangePasswordDialog/ChangePasswordDialog';
import UnlockIcon from '@material-ui/icons/LockOpen';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import Alerts from '../../../pages/Alerts/Alerts';
import clsx from 'clsx';
import SearchInput from './components/SearchInput/SearchInput';
import { useSnackbar } from 'notistack';
import { useConfig } from '../../../eliot-components/src/ConfigProvider/hooks';
import { useUpdateUserMutation } from '../../../store/api-slice';
import { isSelectedOrgDeactivated } from '../../../utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  pullRight: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  logoButton: {
    paddingLeft: 0,
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'baseline',
    },
  },
  userButton: {
    textTransform: 'none',
  },
  'close-button-enter': {
    display: 'none',
  },
  searchIcon: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  searchInput: {
    width: '150px',
    background: 'transparent',
  },
  searchInputChip: {
    maxHeight: '80px',
    overflowY: 'auto',
    '&>div': {
      marginTop: theme.spacing(1.1),
      '&:before': {
        display: 'none',
      },
      '&:after': {
        display: 'none',
      },
    },
  },
  search: {
    paddingLeft: '15px',
    border: 'solid 1px ' + theme.palette.common.white,
    boxShadow: 'none',
    background: 'transparent',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    '& button': {
      padding: '5px',
      '& svg': {
        color: theme.palette.common.white,
      },
    },
  },
  CloseIcon: {
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
    },
  },
  btn: {
    color: 'white',
    '&.Mui-disabled': {
      color: 'inherit',
      opacity: theme.palette.action.disabledOpacity,
    },
    '&.active': {
      '&:after': {
        content: '""',
        backgroundColor: theme.palette.primary.main,
        display: 'block',
        position: 'absolute',
        bottom: -8,
        left: 0,
        height: 3,
        width: '100%',
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
          bottom: -4,
        },
      },
      borderRadius: 0,
      color: 'white',
      backgroundColor: 'transparent',
    },
  },
  scan: {
    '& svg': {
      color: 'white',
    },
  },
  chipInputRoot: {
    backgroundColor: 'transparent',
    width: '100%',
  },
  accountBox: {
    padding: theme.spacing(1, 2, 1, 2),
    '&:focus': {
      outline: 'none',
    },
  },
}));

const Header = memo(() => {
  let isAdvancedSearchInit = false;
  try {
    isAdvancedSearchInit = JSON.parse(
      localStorage.getItem('advancedSearch') || 'undefined'
    );
  } catch {
    // can be ignored
  }

  const history = useHistory();
  const classes = useStyles();
  const alertsRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [isOpen, setIsOpen] = useState(false);
  const { authUser } = useAuthUser();
  const { enqueueSnackbar } = useSnackbar();

  const [userAnchorEl, setUserAnchorEl] = useState<
    (EventTarget & HTMLElement) | null
  >(null);

  const { state, dispatch, toggleDarkMode, isDarkModeActive } =
    useManageContext();
  const { isAppSearchVisible, searchQuery, advancedSearchFilters } = state;

  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);

  const handleLogout = async () => {
    setUserAnchorEl(null);
    window.location.replace('/logout');
  };

  const handleChangePassword = (isSuccess: boolean) => {
    if (isSuccess) {
      enqueueSnackbar('Password successfully changed', { variant: 'success' });
      setIsChangePasswordDialogOpen(false);
    }
  };

  let color: AppBarProps['color'] = 'secondary';
  if (isAppSearchVisible) {
    color = 'default';
  }

  useDeviceSearch(searchQuery, advancedSearchFilters);
  useDeviceScan();

  const isPage = (name: string) => {
    return state.page === name;
  };

  const handleAPIKeyClipboard = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    navigator.clipboard.writeText('Bearer ' + token);
    enqueueSnackbar('API Key copied to clipboard', { variant: 'info' });
    setUserAnchorEl(null);
  };

  const headerIconsWidthHeight = isSmallScreen ? '0.7em' : '0.85em';

  const apiKey = hasUserOrgAnyRole(authUser) ? (
    <span>
      <MenuItem onClick={handleAPIKeyClipboard}>
        <UnlockIcon className={classes.icon} /> API Key
      </MenuItem>
      <Divider />
    </span>
  ) : null;

  const input = useRef(null);

  const actionSearchStarted = () => {
    dispatch({
      type: 'search-started',
    });

    window.setTimeout(() => {
      if (input && input.current) {
        //@ts-ignore
        input.current.focus();
      }
    });
  };
  const [updateUser] = useUpdateUserMutation();
  const { apiBaseURL } = useConfig();
  const [isLoading, setIsLoading] = useState(false);

  const totalMeasurementMode =
    localStorage.getItem('totalMeasurementMode') === 'true';

  let showTargetSystems = false;
  let showDevices = false;

  if (authUser) {
    showTargetSystems = hasOneOfOrgRoles(authUser, authUser.selectedOrgs, [
      Role.DeviceManager,
      Role.DeviceViewer,
    ]);
    showDevices = hasOneOfOrgRoles(authUser, authUser.selectedOrgs, [
      Role.DeviceManager,
      Role.DeviceViewer,
    ]);
  }
  const isOrgDisabled = isSelectedOrgDeactivated(authUser);

  return (
    <AppBar
      position="fixed"
      color={color}
      className={[classes.appBar, 'appBar'].join(' ')}
    >
      <Toolbar>
        <Grid container>
          <Visible when={isAppSearchVisible === true}>
            <Grid item xs={12}>
              <SearchInput
                searchQuery={searchQuery || []}
                inputRef={input}
                advancedSearchOpen={isAdvancedSearchInit}
              />
            </Grid>
          </Visible>
          <Visible when={isAppSearchVisible === false}>
            <Grid item xs={2} sm={5} lg={3}>
              <IconButton
                edge="start"
                onClick={() => {
                  dispatch({ type: 'sidebar-changed' });
                }}
              >
                <MenuIcon className={classes.btn} />
              </IconButton>
              {!state.isEmbeddedApp ? (
                <MediaUp query="sm">
                  <Button
                    size="large"
                    className={classes.logoButton}
                    color="inherit"
                    onClick={() => {
                      history.push('/');
                    }}
                  >
                    Medisanté Manage
                  </Button>
                </MediaUp>
              ) : null}
            </Grid>
            <Grid item xs={10} sm={7} lg={9} className={classes.pullRight}>
              <MediaDown query="md">
                <Tooltip title="Dashboard">
                  <IconButton
                    disabled={isPage('dashboard')}
                    className={clsx(
                      classes.btn,
                      isPage('dashboard') && 'active'
                    )}
                    onClick={() => {
                      history.push('/dashboard');
                    }}
                  >
                    <DashboardIcon
                      style={{
                        width: headerIconsWidthHeight,
                        height: headerIconsWidthHeight,
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Devices">
                  <IconButton
                    disabled={
                      isPage('devices') || !showDevices || isOrgDisabled
                    }
                    className={clsx(classes.btn, isPage('devices') && 'active')}
                    onClick={() => {
                      history.push('/devices');
                    }}
                  >
                    <DevicesIcon
                      style={{
                        width: headerIconsWidthHeight,
                        height: headerIconsWidthHeight,
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Webhooks">
                  <IconButton
                    disabled={
                      isPage('target-systems') ||
                      !showTargetSystems ||
                      isOrgDisabled
                    }
                    className={clsx(
                      classes.btn,
                      isPage('target-systems') && 'active'
                    )}
                    onClick={() => {
                      history.push('/webhooks');
                    }}
                  >
                    <TargetSystemsIcon
                      style={{
                        width: headerIconsWidthHeight,
                        height: headerIconsWidthHeight,
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Alerts">
                  <IconButton
                    disabled={
                      !showTargetSystems || !showDevices || isOrgDisabled
                    }
                    data-testid="alerts-icon"
                    className={classes.btn}
                    onClick={() => setIsOpen(true)}
                    ref={alertsRef}
                  >
                    <ReportProblemIcon
                      style={{
                        width: headerIconsWidthHeight,
                        height: headerIconsWidthHeight,
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                  <Alerts onItemClicked={() => setIsOpen(false)} />
                </Dialog>
              </MediaDown>
              <MediaUp query="lg">
                <MenuOrgs />
                <Tooltip title="Settings">
                  <IconButton
                    data-testid="settings-button"
                    disabled={!authUser.id}
                    className={classes.btn}
                    aria-owns={userAnchorEl ? 'user-menu' : undefined}
                    onClick={(e) => {
                      setUserAnchorEl(e.currentTarget);
                    }}
                  >
                    <SettingsIcon
                      style={{
                        width: headerIconsWidthHeight,
                        height: headerIconsWidthHeight,
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  id="user-menu"
                  style={{ zIndex: 1202 }}
                  anchorEl={userAnchorEl}
                  open={Boolean(userAnchorEl)}
                  onClose={() => {
                    setUserAnchorEl(null);
                  }}
                >
                  <Grid
                    container
                    className={classes.accountBox}
                    direction="column"
                  >
                    <Grid item>
                      <Typography variant="caption">Logged in as</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">
                        {authUser.givenName ||
                          authUser.nickname ||
                          authUser.email}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider />
                  <MenuItem onClick={() => setIsChangePasswordDialogOpen(true)}>
                    <LockIcon className={classes.icon} /> Change Password
                  </MenuItem>
                  <Divider />
                  {apiKey}
                  <MenuItem onClick={toggleDarkMode}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={isDarkModeActive}
                          onClick={toggleDarkMode}
                        />
                      }
                      label="Dark mode"
                    />
                  </MenuItem>

                  {((authUser.globalRoles && authUser.globalRoles.length > 0) ||
                    authUser?.globalRolesModeEnabled) && (
                    <MenuItem
                      disabled={
                        isLoading ||
                        !hasRoleInAnyUsersOrg(
                          authUser,
                          [Role.DeviceManager, Role.DeviceViewer],
                          authUser.orgs
                        )
                      }
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={
                              authUser.globalRoles &&
                              authUser.globalRoles.length > 0
                            }
                            onClick={() => {
                              setIsLoading(true);
                              const enabled =
                                authUser.globalRoles &&
                                authUser.globalRoles.length > 0;
                              updateUser({
                                data: { globalRolesModeEnabled: !enabled },
                                apiBaseURL,
                                id: authUser.id,
                              }).then(() => {
                                // if enabled, don't load all orgs if we have a selected org
                                if (enabled) {
                                  window.localStorage.removeItem(
                                    'user.selected_org'
                                  );
                                }
                                window.location.reload();
                              });
                            }}
                          />
                        }
                        label="Global mode"
                      />
                    </MenuItem>
                  )}

                  <MenuItem disabled={isLoading}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={totalMeasurementMode}
                          onClick={() => {
                            if (totalMeasurementMode) {
                              localStorage.removeItem('totalMeasurementMode');
                            } else {
                              localStorage.setItem(
                                'totalMeasurementMode',
                                'true'
                              );
                            }
                            window.location.reload();
                          }}
                        />
                      }
                      label="Measurement Total"
                    />
                  </MenuItem>
                  <Divider />

                  <MenuItem onClick={handleLogout}>
                    <ClearIcon className={classes.icon} /> Log Out
                  </MenuItem>
                  <Divider />
                  <Version />
                </Menu>
              </MediaUp>
              <Tooltip title="Search">
                <Paper className={classes.search} onClick={actionSearchStarted}>
                  <InputBase
                    margin="dense"
                    className={classes.searchInput}
                    inputProps={{ 'aria-label': 'search' }}
                  />
                  <IconButton disabled={true}>
                    <SearchIcon />
                  </IconButton>
                </Paper>
              </Tooltip>
              <Tooltip className={classes.searchIcon} title="Search">
                <IconButton
                  edge="end"
                  style={{ color: '#fff' }}
                  onClick={actionSearchStarted}
                  data-testid="search-icon"
                >
                  <SearchIcon
                    style={{
                      width: headerIconsWidthHeight,
                      height: headerIconsWidthHeight,
                    }}
                  />
                </IconButton>
              </Tooltip>
              <VisibleToRoles
                roles={[Role.DeviceManager, Role.DeviceViewer]}
                globalOnly={false}
              >
                <span className={classes.scan}>
                  <Scanner
                    target="search"
                    style={{
                      width: headerIconsWidthHeight,
                      height: headerIconsWidthHeight,
                    }}
                  />
                </span>
              </VisibleToRoles>
            </Grid>
          </Visible>
        </Grid>
      </Toolbar>
      <ChangePasswordDialog
        onConfirm={handleChangePassword}
        onClose={() => {
          setIsChangePasswordDialogOpen(false);
        }}
        isOpen={isChangePasswordDialogOpen}
      ></ChangePasswordDialog>
    </AppBar>
  );
});

export default Header;
